// State + actions for the patient's HME relationship (ACDPA-315 / D77).
//
// Same shape as useOrgSharing — plain useState/useCallback. ⚠️ This repo has NO TanStack
// Query (not in package.json, zero usages in src); useOrgSharing is the established pattern
// and this deliberately mirrors it rather than introducing a second data-fetching stack.

import { useCallback, useEffect, useState } from "react";
import {
  MyAssociation,
  MyDeclinedShareRequest,
  MyInvitation,
  getMyOrgRelationship,
  acceptOrgInvitation,
  declineOrgInvitation,
  leaveOrgAssociation,
} from "../../../services/orgRelationship";

export interface UseMyOrgRelationship {
  association: MyAssociation | null;
  invitation: MyInvitation | null;
  /** F-600 (URS_6.9 AC5) — the request an organization declined; optional for existing mocks. */
  declinedShareRequest?: MyDeclinedShareRequest | null;
  loading: boolean;
  /** org_id currently being acted on, for per-card spinners. */
  busyOrgId: number | null;
  toast: string;
  setToast: (msg: string) => void;
  reload: () => Promise<void>;
  accept: (orgId: number) => Promise<boolean>;
  decline: (orgId: number) => Promise<boolean>;
  leave: (orgId: number) => Promise<boolean>;
}

const errorText = (e: unknown, fallback: string): string => {
  const err = e as { response?: { data?: { error?: string } } };
  return err?.response?.data?.error || fallback;
};

export const useMyOrgRelationship = (): UseMyOrgRelationship => {
  const [association, setAssociation] = useState<MyAssociation | null>(null);
  const [invitation, setInvitation] = useState<MyInvitation | null>(null);
  const [declinedShareRequest, setDeclinedShareRequest] = useState<MyDeclinedShareRequest | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [busyOrgId, setBusyOrgId] = useState<number | null>(null);
  const [toast, setToast] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const rel = await getMyOrgRelationship();
      setAssociation(rel.association);
      setInvitation(rel.invitation);
      setDeclinedShareRequest(rel.declined_share_request ?? null);
    } catch (e) {
      // ⚠️ Do NOT surface a toast here. A kernel that predates these fields still answers
      // GET /me perfectly well, and a patient with no HME relationship is the normal case —
      // an error banner on every load would be noise. A failed READ degrades to "no
      // relationship"; only the ACTIONS below report failure, because those the patient asked
      // for and must not be told silently succeeded.
      setAssociation(null);
      setInvitation(null);
      setDeclinedShareRequest(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const act = useCallback(
    async (
      orgId: number,
      fn: (id: number) => Promise<void>,
      ok: string,
      fail: string,
    ): Promise<boolean> => {
      setBusyOrgId(orgId);
      try {
        await fn(orgId);
        setToast(ok);
        await reload();
        return true;
      } catch (e) {
        setToast(errorText(e, fail));
        return false;
      } finally {
        setBusyOrgId(null);
      }
    },
    [reload],
  );

  const accept = useCallback(
    (orgId: number) =>
      act(orgId, acceptOrgInvitation, "Connected.", "Could not accept. Please try again."),
    [act],
  );

  const decline = useCallback(
    (orgId: number) =>
      act(orgId, declineOrgInvitation, "Invitation declined.", "Could not decline. Please try again."),
    [act],
  );

  const leave = useCallback(
    (orgId: number) =>
      act(orgId, leaveOrgAssociation, "You've left.", "Could not leave. Please try again."),
    [act],
  );

  return {
    association,
    invitation,
    declinedShareRequest,
    loading,
    busyOrgId,
    toast,
    setToast,
    reload,
    accept,
    decline,
    leave,
  };
};
